import {Injectable} from '@angular/core';
import {interval, timer} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {TableRecordResult} from '../../model/api/table-record-result';
import {TableWsBaseComponent} from './table-ws-base.component';
import {TableRequest} from '../../model/api/table-request';

export interface SyncWsFacade {
  getSync(viewId: string): any;

  getReSync(): any;

  publishRequest(req: any, viewId: string, requestId: string): void;

  unsubscribeSync?(viewId: string): void;
}

type SyncPerfState = {
  lastReSyncKey?: string;
  lastPublishKey?: string;
  lastRequestId?: string;
};

@Injectable({providedIn: 'root'})
export class TableSyncWsService<T extends TableWsBaseComponent> {

  protected prepareNewRequest(component: T, newRequest: any, currentRequest: any): void {
    // No-op by default.
  }

  protected resolveWs(component: T): SyncWsFacade {
    return (component as any).tableWsService as SyncWsFacade;
  }

  protected buildRequestIfNeeded(component: T): void {
    // No-op by default.
  }

  protected afterReconstructRequest(component: T, request: any): void {
    // No-op by default.
  }

  protected shouldReconstructRequestExtraFields(component: T, current: any, request: any): boolean {
    return false;
  }

  protected onDestroyTopicsExtra(component: T): void {
    // No-op by default.
  }

  protected onAfterSyncApplied(component: T, tableResult: TableRecordResult): void {
    // No-op by default.
  }

  protected configureViewport(component: T): void {
    // No-op by default.
  }

  protected loadList(component: T, result: TableRecordResult): void {
    component.setNotLoading();
  }

  private getReloadInterval(component: T): number {
    const configured = component?.appConfigService?.clearingReloadInterval;
    return configured && configured > 0 ? configured : 250;
  }

  public tryFlushUserRequest(component: T): void {
    if (!component.isWsRefreshEnabled?.()) {
      return;
    }

    if (!component?.tableConfig || !component?.syncTopic) {
      return;
    }

    if (!component.newRequest) {
      return;
    }

    this.prepareNewRequest(component, component.newRequest, component.tableCurrentRequest);

    if (!this.shouldReconstructRequest(component) && !component.forceReload) {
      return;
    }

    const request = (component.newRequest || component.tableCurrentRequest);
    component.newRequest = undefined;
    component.forceReload = false;
    component.tableCurrentRequest = request;

    this.afterReconstructRequest(component, request);
    this.refreshData(component);
  }

  pauseTopics(component: T) {
    if (component.tableUpdateSub) {
      component.tableUpdateSub.unsubscribe();
      component.tableUpdateSub = undefined as any;
    }

    if (component.syncTopic) {
      component.syncTopic.unsubscribe();
      component.syncTopic = undefined;
    }

    if (component.resyncTopic) {
      component.resyncTopic.unsubscribe();
      component.resyncTopic = undefined;
    }

    component.latestWsRequestId = undefined;
  }

  destroyTopics(component: T) {
    const ws = this.resolveWs(component);

    this.pauseTopics(component);

    if (ws.unsubscribeSync && component.wsViewId) {
      ws.unsubscribeSync(component.wsViewId);
    }

    component.records = [];
    component.totalRecords = 0;
    this.onDestroyTopicsExtra(component);
  }

  public refreshCurrentView(component: T): void {
    if (!component.isWsRefreshEnabled?.()) {
      return;
    }

    if (!component.tableCurrentRequest && !component.newRequest) {
      this.buildRequestIfNeeded(component);
    }

    if (component.newRequest) {
      this.prepareNewRequest(component, component.newRequest, component.tableCurrentRequest);

      const request = component.newRequest;
      component.newRequest = undefined;
      component.tableCurrentRequest = request;

      this.afterReconstructRequest(component, request);
    }

    if (!component.tableCurrentRequest) {
      return;
    }

    component.tableCurrentRequest = {
      ...component.tableCurrentRequest,
      syncMetadata: undefined
    };

    component.lastUpdated = undefined;
    component.forceReload = false;
    component.forceDatasetReloadOnce = true;

    this.refreshData(component);
  }

  syncRecordsRequestWs(component: T) {
    if (!component.tableConfig) {
      if (!component.syncTopic) {
        timer(this.getReloadInterval(component))
          .pipe(takeUntil((component as any).destroy$))
          .subscribe(() => {
            if (!component.tableCurrentRequest) {
              this.buildRequestIfNeeded(component);
            }
            this.syncRecordsRequestWs(component);
          });
      }
      return;
    }

    if (!component.tableCurrentRequest) {
      this.buildRequestIfNeeded(component);
    }

    if (component.tableCurrentRequest && !component.tableCurrentRequest.syncMetadata) {
      component.lastUpdated = undefined;
    }

    const ws = this.resolveWs(component);

    if (!component.syncTopic) {
      component.syncTopic = ws.getSync(component.wsViewId)
        .pipe(takeUntil((component as any).destroy$))
        .subscribe((tableResult: TableRecordResult) => {
          if (!tableResult) {
            return;
          }

          if (this.isStaleSyncResult(component, tableResult)) {
            return;
          }

          if (!tableResult.success) {
            component.errorMessage(tableResult.message);
            component.setNotLoading();
            return;
          }

          this.updateRecordsForSync(component, tableResult);

          component.lastUpdated = tableResult.syncMetadata.lastChange;
        });
    }

    if (!component.resyncTopic) {
      component.resyncTopic = ws.getReSync()
        .pipe(takeUntil((component as any).destroy$))
        .subscribe(() => {
          if (!component.isWsRefreshEnabled?.()) {
            return;
          }
          if (component.tableCurrentRequest) {
            component.tableCurrentRequest.syncMetadata = undefined;
          }
          component.forceReload = true;
          component.forceDatasetReloadOnce = true;
        });
    }

    if (component.tableUpdateSub) {
      component.tableUpdateSub.unsubscribe();
    }

    component.tableUpdateSub = interval(this.getReloadInterval(component))
      .pipe(takeUntil((component as any).destroy$))
      .subscribe(() => {
        if (component.newRequest) {
          this.prepareNewRequest(component, component.newRequest, component.tableCurrentRequest);
        }

        const shouldReconstruct = this.shouldReconstructRequest(component);

        if (shouldReconstruct) {
          component.forceReload = false;

          const request = (component.newRequest || component.tableCurrentRequest);
          component.newRequest = undefined;
          component.tableCurrentRequest = request;

          this.afterReconstructRequest(component, request);

          this.refreshData(component);
        }
      });
  }

  public refreshData(component: T) {
    const ws = this.resolveWs(component);
    const rawRequest = component.prepareRequestBeforePost
      ? component.prepareRequestBeforePost(component.tableCurrentRequest)
      : component.tableCurrentRequest;

    const request = {...rawRequest};
    const requestId = `${component.wsViewId}-${++component.wsRequestSeq}`;

    component.latestWsRequestId = requestId;

    if (component.forceDatasetReloadOnce) {
      (request as any).forceDatasetReload = true;
      (request as any).disableSyncMetadataCache = true;
      component.forceDatasetReloadOnce = false;
    }

    (request as any).wsViewId = component.wsViewId;
    (request as any).requestId = requestId;

    ws.publishRequest(request, component.wsViewId, requestId);
  }

  protected isStaleSyncResult(component: T, tableResult: TableRecordResult): boolean {
    const resultViewId = (tableResult as any).wsViewId;
    const resultRequestId = (tableResult as any).requestId;
    const latestRequestId = component.latestWsRequestId;

    if (resultViewId !== component.wsViewId) {
      return true;
    }

    if (resultRequestId && latestRequestId && resultRequestId === latestRequestId) {
      return false;
    }

    if (!resultRequestId || !latestRequestId) {
      return false;
    }

    const resultLastChange = this.toComparableChange(tableResult?.syncMetadata?.lastChange);
    const componentLastChange = this.toComparableChange(component.lastUpdated);

    if (resultLastChange >= componentLastChange) {
      return false;
    }

    return true;
  }

  protected updateRecordsForSync(component: T, tableResult: TableRecordResult): void {
    const sync = tableResult.syncMetadata;

    if (component.tableCurrentRequest && (!component.tableCurrentRequest.first || sync.totalRecord < component.tableCurrentRequest.first)) {
      component.tableCurrentRequest.first = 0;
    }

    if (component.records.length !== sync.totalRecord) {
      component.records.length = sync.totalRecord;
    }

    for (let i = 0; i < sync.totalRecord; i++) {
      if (
        i < component.tableCurrentRequest.first ||
        i > component.tableCurrentRequest.first + component.tableCurrentRequest.rows
      ) {
        component.records[i] = undefined;
      }
    }

    component.totalRecords = sync.totalRecord;

    if (component.tableCurrentRequest && component.lastUpdated !== sync.lastChange) {
      component.tableCurrentRequest.syncMetadata = {...sync, recordIds: undefined};
    }

    this.onAfterSyncApplied(component, tableResult);

    if (component.totalRecords === 0) {
      component.records = [];
      if (component.tableLoadingCount > 0) {
        component.tableLoadingCount = 0;
      }
      component.markForCheck();
      setTimeout(() => component.setNotLoading());
      return;
    }

    if (component.tableCurrentRequest && component.tableCurrentRequest.rows > 0) {
      this.configureViewport(component);

      this.reLoadRecordsRequest(component, tableResult);
    }

    component.markForCheck();

  }

  reLoadRecordsRequest(component: T, tableResult?: TableRecordResult): void {
    if (!component.tableConfig) {
      if (component.tableLoadingCount > 0) {
        component.tableLoadingCount = 0;
      }
      return;
    }
    this.loadList(component, tableResult!);
  }

  protected shouldReconstructRequest(component: T): boolean {
    const request = component.newRequest as TableRequest | undefined;
    const current = component.tableCurrentRequest as TableRequest | undefined;

    if (component.forceReload) {
      return true;
    }

    if (!current) {
      return !!request;
    }

    const currentFilters = current.filters || [];
    const requestFilters = request?.filters || [];
    const currentFilters2 = current.filters2 || [];
    const requestFilters2 = request?.filters2 || [];
    const currentFilters3 = current.filters3 || [];
    const requestFilters3 = request?.filters3 || [];

    const baseDiff =
      (request != undefined && (
        current.first !== request.first ||
        current.rows !== request.rows ||
        currentFilters.length !== requestFilters.length ||
        JSON.stringify(currentFilters) !== JSON.stringify(requestFilters) ||
        JSON.stringify(currentFilters2) !== JSON.stringify(requestFilters2) ||
        JSON.stringify(currentFilters3) !== JSON.stringify(requestFilters3) ||
        current.sortField !== request.sortField ||
        current.sortOrder !== request.sortOrder ||
        current.globalFilter !== request.globalFilter ||
        JSON.stringify(current.multiSortMeta || []) !== JSON.stringify(request.multiSortMeta || [])
      ));

    if (!baseDiff && request) {
      return this.shouldReconstructRequestExtraFields(component, current, request);
    }

    return !!baseDiff;
  }

  private toComparableChange(value: any): number {
    if (value == null) {
      return Number.NEGATIVE_INFINITY;
    }

    if (typeof value === 'number') {
      return value;
    }

    const asDate = new Date(value).getTime();
    return Number.isNaN(asDate) ? Number.NEGATIVE_INFINITY : asDate;
  }
}
